import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, inject, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { SharedService } from '@services/shared.service';
import { firstValueFrom, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { SharedModule } from '@shared/shared.module';
import { PagesService } from '@services/pages.service';
import { QRCodeComponent } from 'angularx-qrcode';
import { environment } from '@env';
import { Clipboard } from '@angular/cdk/clipboard';
import { v4 as uuidv4 } from 'uuid';
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { AuthService } from 'src/app/Auth/AuthService';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { FilterModalComponent, FilterConfig } from '@pages/filter-modal/filter-modal.component';

interface ContestPerformance {
  totalViews: number | null;
  completedTasks: number | null;
  completedTasksIsRate?: boolean;
  avgClickRate: number | null;
  avgCompletionRate: number | null;
  participationRate: number | null;
  totalViewsTrend?: number | null;
  completedTasksTrend?: number | null;
  avgClickRateTrend?: number | null;
  participationRateTrend?: number | null;
  totalViewsTrendLabel?: string | null;
  totalViewsTrendClass?: string | null;
  completedTasksTrendLabel?: string | null;
  completedTasksTrendClass?: string | null;
  avgClickRateTrendLabel?: string | null;
  avgClickRateTrendClass?: string | null;
  participationRateTrendLabel?: string | null;
  participationRateTrendClass?: string | null;
}

interface OverviewStats {
  totalContests: number;
  activeContests: number;
  pendingContests: number;
  finishedContests: number;
  avgClickRate: number;
  avgCompletionRate: number;
  avgParticipationRate: number;
  avgEngagementRate: number;
}

@Component({
  selector: 'app-constant-detail',
  standalone: true,
  imports: [RouterLink, CommonModule, SharedModule, QRCodeComponent, ReactiveFormsModule, FormsModule, NzModalModule, NzSelectModule, NzTableModule, NzToolTipModule, NzDrawerModule, NzDatePickerModule, FilterModalComponent],
  templateUrl: './constant-detail.component.html',
  styleUrls: ['./constant-detail.scss']
})
export class ConstantDetailComponent implements OnInit, OnDestroy {

  contest_detail: any[] = [];
  baseUrl = environment.apiUrl;
  http = inject(HttpClient);
  sharedService = inject(SharedService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  pagesService = inject(PagesService);
  clipboard = inject(Clipboard);
  contests_detail: any = {};
  name!: string;
  created_at!: string;
  description!: string;
  levels_of_contest: any[] = [];
  _contest_prize: any[] = [];
  banners: any[] = [];
  start_date!: string;
  end_date!: string;
  draw_date!: string;
  contestID: any;
  showAllVariables: { [key: string]: boolean } = {};
  maxVariablesToShow: number = 3;
  editingLevels: { [key: string]: boolean } = {};
  levelForms: { [key: string]: FormGroup } = {};
  levelImagePreviews: { [key: string]: string | ArrayBuffer | null } = {};
  isImageModalVisible: { [key: string]: boolean } = {};
  fb = inject(FormBuilder);
  authService = inject(AuthService);

  // Contest Performance and Participants data
  contestPerformance: ContestPerformance = {
    totalViews: null,
    completedTasks: null,
    avgClickRate: null,
    avgCompletionRate: null,
    participationRate: null,
    totalViewsTrend: null,
    completedTasksTrend: null,
    avgClickRateTrend: null,
    participationRateTrend: null,
    totalViewsTrendLabel: null,
    totalViewsTrendClass: null,
    completedTasksTrendLabel: null,
    completedTasksTrendClass: null,
    avgClickRateTrendLabel: null,
    avgClickRateTrendClass: null,
    participationRateTrendLabel: null,
    participationRateTrendClass: null
  };
  performanceStats: OverviewStats = {
    totalContests: 0,
    activeContests: 0,
    pendingContests: 0,
    finishedContests: 0,
    avgClickRate: 0,
    avgCompletionRate: 0,
    avgParticipationRate: 0,
    avgEngagementRate: 0
  };
  performancePreviousStats: OverviewStats = {
    totalContests: 0,
    activeContests: 0,
    pendingContests: 0,
    finishedContests: 0,
    avgClickRate: 0,
    avgCompletionRate: 0,
    avgParticipationRate: 0,
    avgEngagementRate: 0
  };
  performanceTrends: Record<keyof OverviewStats | 'overallSuccessRate', number> = {
    totalContests: 0,
    activeContests: 0,
    pendingContests: 0,
    finishedContests: 0,
    avgClickRate: 0,
    avgCompletionRate: 0,
    avgParticipationRate: 0,
    avgEngagementRate: 0,
    overallSuccessRate: 0
  };
  performanceLoading: boolean = false;
  performanceError: string | null = null;
  performanceDateRange: Date[] = [];
  performancePickerOpen: boolean = false;
  participantsList: any[] = [];
  filteredParticipants: any[] = [];
  participantsLoading: boolean = false;
  winnersList: any[] = [];
  winnersLoading: boolean = false;
  analyticsActivity: any[] = [];
  analyticsLoading: boolean = false;
  analyticsLoaded: boolean = false;
  quizzesList: any[] = [];
  quizzesLoaded: boolean = false;

  // Level Completion Metrics
  levelMetrics: any[] = [];
  levelMetricsLoading: boolean = false;
  levelMetricsError: string = '';

  // Search and Filter for participants
  searchTerm: string = '';
  filterModalVisible: boolean = false;
  currentFilters: any = {};
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  @ViewChild(FilterModalComponent) filterModalComponent!: FilterModalComponent;

  filterConfig: FilterConfig = {
    showTimeline: false,
    showLevel: false,
    showStatus: true,
    showFormat: false,
    showType: false,
    showViewType: false,
    showLocation: true,
    showGender: true,
    showContest: false,
    showAge: false,
    showCompletionRate: false,
    showParticipantName: false,
    showParticipantEmail: false,
    statusOptions: [
      { value: 'in_progress', label: 'In Progress' },
      { value: 'completed', label: 'Completed' }
    ]
  };

  // Drawer for participant levels
  levelsDrawerVisible: boolean = false;
  selectedParticipant: any = null;
  selectedParticipantLevels: any[] = [];

  // Leaderboard
  leaderboard: any[] = [];
  leaderboardLoading: boolean = false;
  leaderboardPage: number = 1;
  leaderboardPerPage: number = 10;
  leaderboardTotal: number = 0;

  // Admin override
  overrideModalVisible: boolean = false;
  overrideParticipantId: any = null;
  overrideReason: string = '';
  overrideSubmitting: boolean = false;
  overrideAuditLog: any[] = [];

  ngOnInit(): void {
    // Setup debounced search for participants
    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.applyClientFilters();
    });

    const encodedUid = this.route.snapshot.queryParamMap.get('id');

    if (encodedUid) {
      const storedId = localStorage.getItem(encodedUid);

      if (storedId) {
        this.contestID = Number(storedId);
        this.getContestPerformance(this.contestID);
        this.getContestDetails(this.contestID);
        this.getLevelCompletionMetrics(this.contestID);
      } else {
        this.sharedService.createNotification('error', 'Invalid or expired contest link.');
        this.router.navigate(['/home/contests']);
      }
    } else {
      this.sharedService.createNotification('error', 'No contest ID provided in the URL.');
      this.router.navigate(['/home/contests']);
    }
  }

  async getLevelCompletionMetrics(contestId: number): Promise<void> {
    this.levelMetricsLoading = true;
    this.levelMetricsError = '';
    try {
      const response: any = await firstValueFrom(
        this.pagesService.getAll('CONTEST_LEVEL_COMPLETION_METRICS', { contest_id: contestId })
      );
      this.levelMetrics = Array.isArray(response) ? response : (response?.items ?? response?.data ?? []);
    } catch {
      this.levelMetricsError = 'Level metrics unavailable.';
      this.levelMetrics = [];
    } finally {
      this.levelMetricsLoading = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  editContest() {
    if (!this.isContestEditable()) {
      this.sharedService.createNotification('error', 'This contest cannot be edited because it is approved or completed.');
      return;
    }

    const id = this.contestID;
    const uid = uuidv4(); // Generate a unique UID for this contest

    // Store the mapping temporarily (UID → actual numeric ID)
    localStorage.setItem(uid, id.toString());

    // Navigate using the UID instead of the real numeric ID
    this.router.navigate(['/home/contests/edit'], { queryParams: { id: uid } });
  }

  isContestEditable(): boolean {
    if (!this.contests_detail || !this.contests_detail.status) {
      return true; // Allow edit if status is not available
    }

    const status = this.contests_detail.status.toLowerCase();
    return status !== 'approved' && status !== 'complete' && status !== 'completed';
  }

  viewContestAnalytics() {
    this.router.navigate(['/home/analytic/monitoring'], {
      queryParams: { contest_id: this.contestID },
    });
  }
  async getContestDetails(id: string) {
    this.sharedService.showLoader();
    try {
      const response: any = await firstValueFrom(this.pagesService.getById('GET_SINGLE_CONTEST', id));
      if (response) {
        const contestData = response;
        this.contests_detail = contestData;
        this.name = contestData.name;
        this.created_at = contestData.created_at;
        this.description = contestData.details;

        // Sort levels by their order or creation sequence
        this.levels_of_contest = contestData._levels_of_contest.sort((a: any, b: any) => {
          // If there's an 'order' field, use it
          if (a.order !== undefined && b.order !== undefined) {
            return a.order - b.order;
          }
          // Otherwise sort by id (assuming sequential ids)
          return a.id - b.id;
        });

        this.banners = contestData.banners;
        this.start_date = this.formatDate(contestData.start_date);
        this.end_date = this.formatDate(contestData.end_date);
        this.draw_date = this.resolveDrawDate(contestData);
        this._contest_prize = contestData._contest_prize;

        this.levels_of_contest.forEach(level => {
          this.showAllVariables[level.id] = false;
        });

        // Pre-load quizzes if any level has quiz type
        const hasQuizLevel = this.levels_of_contest.some((level: any) =>
          (level.level_type || level.type) === 'quiz'
        );
        if (hasQuizLevel && !this.quizzesLoaded) {
          this.getQuizzesRecords();
        }

        // Fetch participants data and analytics for this contest
        await this.getContestParticipants(id);
        await this.getContestAnalytics(id);
        await this.buildLeaderboard();

        this.applyPerformanceFallbacks();
      }
    } catch (error) {
      this.sharedService.showError(error, 'Failed to fetch contest details.');
    } finally {
      this.sharedService.hideLoader();
    }
  }

  async getContestParticipants(contestId: string) {
    this.participantsLoading = true;
    this.winnersLoading = true;
    try {
      // Use GET request with query parameter instead of path parameter
      const response: any = await firstValueFrom(
        this.pagesService.getByCustomName('GET_Total_PARTICIPANT', 'contest_id', contestId)
      );

      const items = Array.isArray(response)
        ? response
        : Array.isArray(response?.items)
          ? response.items
          : [];

      if (items.length > 0) {
        const normalizedParticipants = items.map((participant: any) =>
          this.normalizeContestParticipant(participant)
        );

        const sortedParticipants = [...normalizedParticipants].sort(
          (a: any, b: any) =>
            this.getParticipationDateTimestamp(b) - this.getParticipationDateTimestamp(a)
        );

        this.participantsList = sortedParticipants;
        this.filteredParticipants = sortedParticipants;

        // Extract winners from participants
        this.extractWinners();
        this.applyPerformanceFallbacks();
      } else {
        // If response is not an array, set empty arrays
        this.participantsList = [];
        this.filteredParticipants = [];
        this.winnersList = [];
        this.applyPerformanceFallbacks();
      }
    } catch (error: any) {
      // Handle error silently - just set empty arrays
      this.participantsList = [];
      this.filteredParticipants = [];
      this.winnersList = [];
      this.applyPerformanceFallbacks();

      // Don't show error notification for ERROR_CODE_NOT_FOUND
      if (error?.error?.code !== 'ERROR_CODE_NOT_FOUND') {
        console.error('Failed to fetch participants:', error);
      }
    } finally {
      this.participantsLoading = false;
      this.winnersLoading = false;
    }
  }

  async getContestAnalytics(contestId: string) {
    this.analyticsLoading = true;
    try {
      const response: any = await firstValueFrom(
        this.pagesService.getByIdByQuerry('ANALYSIS_URL_Reports_DETAIL', contestId)
      );
      const dataArray = Array.isArray(response) ? response : (response?.data || []);
      this.analyticsActivity = Array.isArray(dataArray) ? dataArray : [];
    } catch (error: any) {
      this.analyticsActivity = [];
      if (error?.error?.code !== 'ERROR_CODE_NOT_FOUND') {
        console.error('Failed to fetch contest analytics:', error);
      }
    } finally {
      this.analyticsLoading = false;
      this.analyticsLoaded = true;
    }
  }

  private normalizeContestParticipant(participant: any): any {
    if (!participant || typeof participant !== 'object') {
      return participant;
    }

    const normalized = { ...participant };

    const contestCandidate =
      participant?.contest ||
      participant?._contest ||
      participant?.contest_detail ||
      participant?.contestDetail ||
      participant?.contest_details ||
      participant?.contestDetails ||
      this.contests_detail ||
      null;

    if (contestCandidate && !participant?.contest) {
      normalized.contest = contestCandidate;
    }

    const endUserCandidate =
      participant?._end_user ||
      participant?.end_user ||
      participant?.endUser ||
      participant?.user ||
      participant?.participant ||
      null;

    if (endUserCandidate && !participant?._end_user) {
      normalized._end_user = endUserCandidate;
    }

    const statusCandidate =
      participant?.status ||
      participant?.participant_status ||
      participant?.contestParticipant?.status ||
      participant?.contest_participant?.[0]?.status ||
      participant?._contest_participant?.[0]?.status ||
      participant?.contest_participants?.[0]?.status ||
      null;

    if (statusCandidate && !participant?.status) {
      normalized.status = statusCandidate;
    }

    if (statusCandidate && !participant?.participant_status) {
      normalized.participant_status = statusCandidate;
    }

    if (!participant?.levels_detail && participant?.levelsDetail) {
      normalized.levels_detail = participant.levelsDetail;
    }

    if (!participant?.levelsDetail && participant?.levels_detail) {
      normalized.levelsDetail = participant.levels_detail;
    }

    return normalized;
  }

  private getParticipationDateTimestamp(participant: any): number {
    const participationDate =
      participant?.participation_date ||
      participant?.join_date ||
      participant?.joined_at ||
      participant?.created_at ||
      participant?.contest_join_date ||
      participant?.contestParticipant?.created_at ||
      participant?.contest_participant?.[0]?.created_at ||
      participant?._contest_participant?.[0]?.created_at ||
      null;

    if (!participationDate) {
      return 0;
    }

    const timestamp = new Date(participationDate).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }

  getParticipationDate(participant: any): string | null {
    const participationDate =
      participant?.participation_date ||
      participant?.join_date ||
      participant?.joined_at ||
      participant?.created_at ||
      participant?.contest_join_date ||
      participant?.contestParticipant?.created_at ||
      participant?.contest_participant?.[0]?.created_at ||
      participant?._contest_participant?.[0]?.created_at ||
      null;

    return participationDate || null;
  }

  getParticipantName(participant: any): string {
    return (
      participant?._end_user?.name ||
      participant?.end_user?.name ||
      participant?.participant_name ||
      participant?.userName ||
      participant?.name ||
      'N/A'
    );
  }

  getParticipantGender(participant: any): string {
    return (
      participant?._end_user?.gender ||
      participant?.end_user?.gender ||
      participant?.participant_gender ||
      participant?.participant_gender ||
      participant?.gender ||
      'N/A'
    );
  }

  getParticipantAge(participant: any): string | number {
    const directAge =
      participant?._end_user?.age ??
      participant?.end_user?.age ??
      participant?.participant_age ??
      participant?.age;

    if (directAge !== null && directAge !== undefined && directAge !== '') {
      return directAge;
    }

    const dob =
      participant?._end_user?.date_of_birth ||
      participant?._end_user?.dob ||
      participant?.end_user?.date_of_birth ||
      participant?.end_user?.dob ||
      participant?.participant_dob ||
      participant?.date_of_birth ||
      participant?.dob;

    if (!dob) {
      return 'N/A';
    }

    const birthDate = new Date(dob);
    if (Number.isNaN(birthDate.getTime())) {
      return 'N/A';
    }

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age >= 0 ? age : 'N/A';
  }

  getWinnerCompletionTimestamp(winner: any): string | null {
    const completionTimestamp =
      winner?.completed_at ||
      winner?.completion_date ||
      winner?.completion_timestamp ||
      winner?.updated_at ||
      winner?.modified_at ||
      null;

    return completionTimestamp || null;
  }

  getWinnerPrizeStatus(winner: any): string {
    const participantId = winner?.participant_id ?? winner?._end_user?.id;
    const nestedPrizeHistoryStatus =
      winner?.contest?.prizes?.[0]?.prize_history?.find(
        (history: any) => String(history?.end_user_id) === String(participantId)
      )?.status;

    return (
      winner?.prize_status ||
      winner?.prizeStatus ||
      nestedPrizeHistoryStatus ||
      (winner?.is_winner ? 'Winner' : 'N/A')
    );
  }

  getWinnerPrizeStatusClass(winner: any): string {
    const rawStatus = (this.getWinnerPrizeStatus(winner) || '').toLowerCase().trim();
    const status = rawStatus.replace(/_/g, ' ');

    if (status === 'pending') return 'prize-status pending';
    if (status === 'processing') return 'prize-status processing';
    if (status === 'ready for dispatch') return 'prize-status ready-dispatch';
    if (status === 'shipped') return 'prize-status shipped';
    if (status === 'in transit') return 'prize-status in-transit';
    if (status === 'out for delivery') return 'prize-status out-delivery';
    if (status === 'delivered' || status === 'completed' || status === 'winner') return 'prize-status delivered';
    if (status === 'returned') return 'prize-status returned';
    if (status === 'rejected' || status === 'cancelled' || status === 'failed') return 'prize-status failed';

    return 'prize-status default';
  }

  extractWinners() {
    this.winnersList = [];

    // Winner source can come from single contest payload OR nested inside participant.contest.
    const contestDraws = Array.isArray(this.contests_detail?.contest_draw)
      ? this.contests_detail.contest_draw
      : Array.isArray(this.participantsList?.[0]?.contest?.contest_draw)
        ? this.participantsList[0].contest.contest_draw
        : [];

    if (!Array.isArray(contestDraws) || contestDraws.length === 0) {
      return;
    }

    // Normalize IDs as strings to avoid number/string mismatch issues.
    const winnerIds = new Set<string>();
    contestDraws.forEach((draw: any) => {
      if (Array.isArray(draw?.winner_of_contest_draw)) {
        draw.winner_of_contest_draw.forEach((winner: any) => {
          if (winner?.winner_id !== null && winner?.winner_id !== undefined) {
            winnerIds.add(String(winner.winner_id));
          }
        });
      }
    });

    if (winnerIds.size === 0) {
      return;
    }

    this.winnersList = this.participantsList.filter((participant: any) => {
      const participantId =
        participant?.participant_id ??
        participant?.user_id ??
        participant?.end_user?.id ??
        participant?._end_user?.id;

      return participantId !== null &&
        participantId !== undefined &&
        winnerIds.has(String(participantId));
    });
  }

  async buildLeaderboard(page: number = 1): Promise<void> {
    if (this.contests_detail?.draw_type !== 'top_points') return;
    this.leaderboardLoading = true;
    try {
      const response: any = await firstValueFrom(
        this.pagesService.getAll('CONTEST_LEADERBOARD', {
          contest_id: this.contestID,
          page: page - 1,
          per_page: this.leaderboardPerPage
        })
      );
      const items = Array.isArray(response) ? response : (response?.items || response?.data || []);
      this.leaderboardTotal = response?.total ?? response?.itemsReceived ?? items.length;
      this.leaderboardPage = page;
      this.leaderboard = items.map((p: any, i: number) => ({
        ...p,
        _rank: p?.rank ?? ((page - 1) * this.leaderboardPerPage + i + 1),
        _points: Number(p?.points ?? p?.total_points ?? p?.score ?? 0),
        _name: p?.name ?? p?.participant_name ?? this.getParticipantName(p),
        _gender: p?.gender ?? this.getParticipantGender(p)
      }));
    } catch {
      this.leaderboard = [];
    } finally {
      this.leaderboardLoading = false;
    }
  }

  getDrawTypeLabel(): string {
    const dt = this.contests_detail?.draw_type || '';
    const map: Record<string, string> = {
      top_points: 'Top Points',
      first_to_complete: 'First to Complete',
      random: 'Random Draw',
      manual: 'Manual',
    };
    return map[dt] || dt;
  }

  getTieBreakingRule(): string {
    const dt = this.contests_detail?.draw_type || '';
    const map: Record<string, string> = {
      top_points: 'Tie-breaking: Earlier completion time wins',
      first_to_complete: 'Tie-breaking: Millisecond completion timestamp',
      random: 'No tie-breaking (random selection)',
      manual: 'Admin determines winner manually',
    };
    return map[dt] || '';
  }

  openOverrideModal(): void {
    this.overrideModalVisible = true;
    this.overrideParticipantId = null;
    this.overrideReason = '';
    this.loadAuditLog();
  }

  closeOverrideModal(): void {
    this.overrideModalVisible = false;
  }

  async loadAuditLog(): Promise<void> {
    try {
      const response: any = await firstValueFrom(
        this.pagesService.getByCustomName('WINNER_OVERRIDE_AUDIT_LOG', 'contest_id', this.contestID)
      );
      this.overrideAuditLog = Array.isArray(response) ? response : (response?.data || response?.items || []);
    } catch {
      this.overrideAuditLog = [];
    }
  }

  async submitOverride(): Promise<void> {
    if (!this.overrideParticipantId || !this.overrideReason.trim()) {
      this.sharedService.createNotification('error', 'Please select a participant and provide a reason.');
      return;
    }
    this.overrideSubmitting = true;
    try {
      await firstValueFrom(
        this.pagesService.update('WINNER_OVERRIDE', {
          contest_id: this.contestID,
          participant_id: this.overrideParticipantId,
          reason: this.overrideReason.trim()
        })
      );
      this.sharedService.createNotification('success', 'Winner overridden successfully.');
      this.overrideModalVisible = false;
      await this.getContestParticipants(this.contestID);
    } catch (error) {
      this.sharedService.showError(error, 'Failed to override winner.');
    } finally {
      this.overrideSubmitting = false;
    }
  }

  private coerceNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'object') {
      if (value === null) return null;
      if (Object.prototype.hasOwnProperty.call(value, 'value')) {
        return this.coerceNumber(value.value);
      }
      if (Object.prototype.hasOwnProperty.call(value, 'count')) {
        return this.coerceNumber(value.count);
      }
    }
    if (typeof value === 'string') {
      const cleaned = value.replace(/[%\,]/g, '').trim();
      if (cleaned === '') return null;
      const parsed = Number(cleaned);
      return Number.isFinite(parsed) ? parsed : null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private extractNumber(payload: any, keys: string[]): number | null {
    if (!payload || typeof payload !== 'object') return null;
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(payload, key)) {
        const parsed = this.coerceNumber(payload[key]);
        if (parsed !== null) {
          return parsed;
        }
      }
    }
    return null;
  }

  private extractString(payload: any, keys: string[]): string | null {
    if (!payload || typeof payload !== 'object') return null;
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(payload, key)) {
        const value = payload[key];
        if (value === null || value === undefined) continue;
        const normalized = String(value).trim();
        if (normalized !== '') {
          return normalized;
        }
      }
    }
    return null;
  }

  private extractTrendNumber(payload: any, keys: string[]): number | null {
    if (!payload || typeof payload !== 'object') return null;
    for (const key of keys) {
      if (!Object.prototype.hasOwnProperty.call(payload, key)) {
        continue;
      }
      const raw = payload[key];
      if (raw && typeof raw === 'object') {
        if (Object.prototype.hasOwnProperty.call(raw, 'trend')) {
          const parsedTrend = this.coerceNumber((raw as any).trend);
          if (parsedTrend !== null) {
            return parsedTrend;
          }
        }
        continue;
      }
      const parsed = this.coerceNumber(raw);
      if (parsed !== null) {
        return parsed;
      }
    }
    return null;
  }

  private normalizeRate(value: number | null): number | null {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return null;
    }
    const abs = Math.abs(value);
    if (abs > 100) {
      return value / 100;
    }
    if (abs > 0 && abs <= 1) {
      return value * 100;
    }
    return value;
  }

  private unwrapPerformanceResponse(response: any): any {
    if (!response) return null;
    const candidate = response?.data ?? response?.result ?? response?.body ?? response;
    if (Array.isArray(candidate)) {
      return candidate[0] ?? null;
    }
    if (candidate?.performance) {
      return candidate.performance;
    }
    if (candidate?.contestPerformance) {
      return candidate.contestPerformance;
    }
    return candidate;
  }

  private resolveTrendClass(label: string | null): string | null {
    if (!label) return null;
    const normalized = label.toLowerCase();
    if (normalized.includes('lower') || normalized.includes('down') || normalized.includes('decrease') || normalized.startsWith('-')) {
      return 'bg-danger';
    }
    if (normalized.includes('higher') || normalized.includes('up') || normalized.includes('increase') || normalized.startsWith('+')) {
      return 'bg-success';
    }
    return 'bg-secondary';
  }

  private buildEmptyPerformance(): ContestPerformance {
    return {
      totalViews: null,
      completedTasks: null,
      completedTasksIsRate: false,
      avgClickRate: null,
      avgCompletionRate: null,
      participationRate: null,
      totalViewsTrend: null,
      completedTasksTrend: null,
      avgClickRateTrend: null,
      participationRateTrend: null,
      totalViewsTrendLabel: null,
      totalViewsTrendClass: null,
      completedTasksTrendLabel: null,
      completedTasksTrendClass: null,
      avgClickRateTrendLabel: null,
      avgClickRateTrendClass: null,
      participationRateTrendLabel: null,
      participationRateTrendClass: null
    };
  }

  private normalizeContestPerformance(response: any): ContestPerformance {
    const payload = this.unwrapPerformanceResponse(response);
    if (!payload || typeof payload !== 'object') {
      return this.buildEmptyPerformance();
    }

    const totalViews = this.extractNumber(payload, [
      'total_views', 'totalViews', 'total_view', 'views', 'view', 'total_view_count', 'totalViewCount'
    ]);

    const completedTasks = this.extractNumber(payload, [
      'completed_tasks', 'completedTasks', 'completed', 'completed_count', 'tasks_completed', 'total_completed_tasks'
    ]);

    const avgClickRate = this.extractNumber(payload, [
      'avg_click_rate', 'avgClickRate', 'avg_clicks_rate', 'avg_click_through_rate', 'click_rate', 'clickRate',
      'engagement_rate', 'engagementRate'
    ]);

    const avgCompletionRate = this.extractNumber(payload, [
      'avg_completion_rate', 'avgCompletionRate', 'completion_rate', 'completionRate',
      'average_completion_rate', 'averageCompletionRate'
    ]);

    const participationRate = this.extractNumber(payload, [
      'participation_rate', 'participationRate', 'participation_rate_percent', 'participationPercentage'
    ]);

    const totalViewsTrend = this.extractTrendNumber(payload, [
      'totalViews', 'total_views', 'total_view', 'views', 'view',
      'total_views_trend', 'totalViewsTrend', 'total_views_trend_label', 'views_trend', 'viewsTrend'
    ]);
    const completedTasksTrend = this.extractTrendNumber(payload, [
      'completedTasks', 'completed_tasks', 'completed', 'completed_count',
      'completed_tasks_trend', 'completedTasksTrend', 'completed_trend', 'completedTrend'
    ]);
    const avgClickRateTrend = this.extractTrendNumber(payload, [
      'avgClickRate', 'avg_click_rate', 'averageCTR',
      'avg_click_rate_trend', 'avgClickRateTrend', 'click_rate_trend', 'clickRateTrend', 'averageCTRTrend'
    ]);
    const participationRateTrend = this.extractTrendNumber(payload, [
      'participationRate', 'participation_rate', 'participation_rate_trend', 'participationRateTrend', 'participation_trend', 'participationTrend'
    ]);

    const buildTrendLabel = (trend: number | null): string | null => {
      if (trend === null || trend === undefined || Number.isNaN(trend)) return null;
      const direction = trend >= 0 ? 'Higher' : 'Lower';
      return `${Math.abs(trend).toFixed(0)}% ${direction}`;
    };

    const totalViewsTrendLabel = this.extractString(payload, [
      'total_views_trend', 'totalViewsTrend', 'total_views_trend_label', 'views_trend', 'viewsTrend'
    ]) ?? buildTrendLabel(totalViewsTrend);
    const completedTasksTrendLabel = this.extractString(payload, [
      'completed_tasks_trend', 'completedTasksTrend', 'completed_trend', 'completedTrend'
    ]) ?? buildTrendLabel(completedTasksTrend);
    const avgClickRateTrendLabel = this.extractString(payload, [
      'avg_click_rate_trend', 'avgClickRateTrend', 'click_rate_trend', 'clickRateTrend'
    ]) ?? buildTrendLabel(avgClickRateTrend);
    const participationRateTrendLabel = this.extractString(payload, [
      'participation_rate_trend', 'participationRateTrend', 'participation_trend', 'participationTrend'
    ]) ?? buildTrendLabel(participationRateTrend);

    const totalViewsTrendClass = totalViewsTrendLabel
      ? this.resolveTrendClass(totalViewsTrendLabel)
      : totalViewsTrend !== null
        ? (totalViewsTrend >= 0 ? 'bg-success' : 'bg-danger')
        : null;
    const completedTasksTrendClass = completedTasksTrendLabel
      ? this.resolveTrendClass(completedTasksTrendLabel)
      : completedTasksTrend !== null
        ? (completedTasksTrend >= 0 ? 'bg-success' : 'bg-danger')
        : null;
    const avgClickRateTrendClass = avgClickRateTrendLabel
      ? this.resolveTrendClass(avgClickRateTrendLabel)
      : avgClickRateTrend !== null
        ? (avgClickRateTrend >= 0 ? 'bg-success' : 'bg-danger')
        : null;
    const participationRateTrendClass = participationRateTrendLabel
      ? this.resolveTrendClass(participationRateTrendLabel)
      : participationRateTrend !== null
        ? (participationRateTrend >= 0 ? 'bg-success' : 'bg-danger')
        : null;

    const normalizedAvgCompletionRate = this.normalizeRate(avgCompletionRate);
    const normalizedAvgClickRate = this.normalizeRate(avgClickRate);
    const normalizedParticipationRate = this.normalizeRate(participationRate);

    const hasCompletedTasks = completedTasks !== null && completedTasks !== undefined;
    const resolvedCompletedTasks = hasCompletedTasks ? completedTasks : normalizedAvgCompletionRate;
    const completedTasksIsRate = !hasCompletedTasks && normalizedAvgCompletionRate !== null;

    return {
      totalViews,
      completedTasks: resolvedCompletedTasks,
      completedTasksIsRate,
      avgClickRate: normalizedAvgClickRate,
      avgCompletionRate: normalizedAvgCompletionRate,
      participationRate: normalizedParticipationRate,
      totalViewsTrend,
      completedTasksTrend,
      avgClickRateTrend,
      participationRateTrend,
      totalViewsTrendLabel,
      totalViewsTrendClass,
      completedTasksTrendLabel,
      completedTasksTrendClass,
      avgClickRateTrendLabel,
      avgClickRateTrendClass,
      participationRateTrendLabel,
      participationRateTrendClass
    };
  }

  private buildEmptyPerformanceOverview(): OverviewStats {
    return {
      totalContests: 0,
      activeContests: 0,
      pendingContests: 0,
      finishedContests: 0,
      avgClickRate: 0,
      avgCompletionRate: 0,
      avgParticipationRate: 0,
      avgEngagementRate: 0
    };
  }

  private updatePerformanceOverview(response: any): void {
    const payload = this.unwrapPerformanceResponse(response);
    if (!payload || typeof payload !== 'object') {
      this.performanceStats = this.buildEmptyPerformanceOverview();
      this.performancePreviousStats = this.buildEmptyPerformanceOverview();
      this.performanceTrends = {
        totalContests: 0,
        activeContests: 0,
        pendingContests: 0,
        finishedContests: 0,
        avgClickRate: 0,
        avgCompletionRate: 0,
        avgParticipationRate: 0,
        avgEngagementRate: 0,
        overallSuccessRate: 0
      };
      return;
    }

    const readValue = (obj: any, fallback: number = 0): number => {
      if (obj == null) return fallback;
      if (typeof obj === 'number') return obj;
      if (typeof obj?.value === 'number') return obj.value;
      return fallback;
    };
    const readTrend = (obj: any): number | null => {
      if (obj == null) return null;
      if (typeof obj?.trend === 'number') return obj.trend;
      return null;
    };
    const prevFromTrend = (current: number, trend: number | null): number => {
      if (trend == null || !isFinite(trend)) return 0;
      const denom = 1 + trend / 100;
      if (denom === 0) return 0;
      return current / denom;
    };

    const totalViews = readValue(
      payload?.view,
      readValue(payload?.views, readValue(payload?.totalViews, readValue(payload?.total_views, 0)))
    );
    const totalClicks = readValue(
      payload?.click,
      readValue(payload?.clicks, readValue(payload?.totalClicks, readValue(payload?.total_clicks, 0)))
    );
    const participants = readValue(
      payload?.participants,
      readValue(payload?.totalParticipants, readValue(payload?.total_participants, 0))
    );
    const completed = readValue(
      payload?.completed,
      readValue(payload?.completedTasks, readValue(payload?.completed_tasks, 0))
    );

    const overallSuccessRateRaw = readValue(
      payload?.overallSuccessRate,
      readValue(payload?.overall_success_rate, readValue(payload?.averageClickThroughRate, readValue(payload?.averageCTR, 0)))
    );
    const avgCompletionRaw = readValue(
      payload?.completionRate,
      readValue(payload?.avgCompletionRate, readValue(payload?.averageCompletionRate, readValue(payload?.avg_completion_rate, 0)))
    );
    const avgParticipationRaw = readValue(
      payload?.participationRate,
      readValue(payload?.avgParticipationRate, readValue(payload?.averageParticipationRate, readValue(payload?.avg_participation_rate, 0)))
    );
    const avgEngagementRaw = readValue(
      payload?.engagementRate,
      readValue(payload?.avgEngagementRate, readValue(payload?.averageEngagementRate, readValue(payload?.avg_engagement_rate, 0)))
    );

    const overallSuccessRate = this.normalizeRate(overallSuccessRateRaw) ?? 0;
    const avgCompletion = this.normalizeRate(avgCompletionRaw) ?? 0;
    const avgParticipation = this.normalizeRate(avgParticipationRaw) ?? 0;
    const avgEngagement = this.normalizeRate(avgEngagementRaw) ?? 0;

    const totalTrend = readTrend(payload?.view) ?? readTrend(payload?.views) ?? readTrend(payload?.totalViews);
    const activeTrend = readTrend(payload?.click) ?? readTrend(payload?.clicks) ?? readTrend(payload?.totalClicks);
    const pendingTrend = readTrend(payload?.participants) ?? readTrend(payload?.totalParticipants);
    const finishedTrend = readTrend(payload?.completed) ?? readTrend(payload?.completedTasks);
    const overallTrend = readTrend(payload?.overallSuccessRate) ?? readTrend(payload?.averageClickThroughRate);
    const completionTrend = readTrend(payload?.completionRate) ?? readTrend(payload?.avgCompletionRate) ?? readTrend(payload?.averageCompletionRate);
    const participationTrend = readTrend(payload?.participationRate) ?? readTrend(payload?.avgParticipationRate) ?? readTrend(payload?.averageParticipationRate);
    const engagementTrend = readTrend(payload?.engagementRate) ?? readTrend(payload?.avgEngagementRate) ?? readTrend(payload?.averageEngagementRate);

    this.performanceStats = {
      totalContests: totalViews,
      activeContests: totalClicks,
      pendingContests: participants,
      finishedContests: completed,
      avgClickRate: overallSuccessRate,
      avgCompletionRate: avgCompletion,
      avgParticipationRate: avgParticipation,
      avgEngagementRate: avgEngagement
    };

    this.performancePreviousStats = {
      totalContests: prevFromTrend(totalViews, totalTrend),
      activeContests: prevFromTrend(totalClicks, activeTrend),
      pendingContests: prevFromTrend(participants, pendingTrend),
      finishedContests: prevFromTrend(completed, finishedTrend),
      avgClickRate: prevFromTrend(overallSuccessRate, overallTrend),
      avgCompletionRate: prevFromTrend(avgCompletion, completionTrend),
      avgParticipationRate: prevFromTrend(avgParticipation, participationTrend),
      avgEngagementRate: prevFromTrend(avgEngagement, engagementTrend)
    };

    this.performanceTrends = {
      totalContests: totalTrend ?? 0,
      activeContests: activeTrend ?? 0,
      pendingContests: pendingTrend ?? 0,
      finishedContests: finishedTrend ?? 0,
      avgClickRate: overallTrend ?? 0,
      avgCompletionRate: completionTrend ?? 0,
      avgParticipationRate: participationTrend ?? 0,
      avgEngagementRate: engagementTrend ?? 0,
      overallSuccessRate: overallTrend ?? 0
    };
  }

  async getContestPerformance(contestId: number | string): Promise<void> {
    this.performanceLoading = true;
    this.performanceError = null;

    try {
      const parsedContestId = Number(contestId);
      if (!Number.isFinite(parsedContestId)) {
        throw new Error('Invalid contest id for performance request.');
      }
      const startDate = this.performanceDateRange?.[0] ?? null;
      const endDate = this.performanceDateRange?.[1] ?? null;
      const params = {
        start_date: startDate ? Math.floor(startDate.getTime() / 1000) : '',
        end_date: endDate ? Math.floor(endDate.getTime() / 1000) : '',
        contest_id: parsedContestId
      };
      const response = await firstValueFrom(
        this.pagesService.getAllById('CONTEST_DETAIL_OVERVIEW', params)
      );
      this.contestPerformance = this.normalizeContestPerformance(response);
      this.updatePerformanceOverview(response);
      this.applyPerformanceFallbacks();
    } catch (error: any) {
      this.contestPerformance = this.buildEmptyPerformance();
      this.performanceStats = this.buildEmptyPerformanceOverview();
      this.performancePreviousStats = this.buildEmptyPerformanceOverview();
      this.performanceTrends = {
        totalContests: 0,
        activeContests: 0,
        pendingContests: 0,
        finishedContests: 0,
        avgClickRate: 0,
        avgCompletionRate: 0,
        avgParticipationRate: 0,
        avgEngagementRate: 0,
        overallSuccessRate: 0
      };
      this.performanceError = 'Performance data unavailable.';
      if (error?.error?.code !== 'ERROR_CODE_NOT_FOUND') {
        console.error('Failed to fetch contest performance:', error);
      }
      this.applyPerformanceFallbacks();
    } finally {
      this.performanceLoading = false;
    }
  }

  private getUniqueParticipantsCount(): number | null {
    if (!Array.isArray(this.participantsList) || this.participantsList.length === 0) {
      return null;
    }

    const participantIds = new Set<string>();
    this.participantsList.forEach((p: any) => {
      const id = p?.participant_id ?? p?.user_id ?? p?._end_user?.id ?? p?.id;
      if (id !== null && id !== undefined) {
        participantIds.add(String(id));
      }
    });

    return participantIds.size > 0 ? participantIds.size : this.participantsList.length;
  }

  private getContestTotalViewsFallback(): number | null {
    return this.extractNumber(this.contests_detail, [
      'total_views', 'totalViews', 'views', 'view', 'total_view_count', 'total_view'
    ]);
  }

  private applyPerformanceFallbacks(): void {
    if (this.performanceLoading) return;

    const nextPerformance: ContestPerformance = { ...this.contestPerformance };
    const fallbackViews = this.getContestTotalViewsFallback();
    const fallbackParticipants = this.getUniqueParticipantsCount();

    if ((nextPerformance.totalViews === null || nextPerformance.totalViews === 0) && fallbackViews && fallbackViews > 0) {
      nextPerformance.totalViews = fallbackViews;
    }

    if ((nextPerformance.participationRate === null || nextPerformance.participationRate === 0) &&
      fallbackViews && fallbackViews > 0 &&
      fallbackParticipants && fallbackParticipants > 0) {
      nextPerformance.participationRate = this.normalizeRate((fallbackParticipants / fallbackViews) * 100);
    }

    this.contestPerformance = nextPerformance;
  }

  formatMetric(value: number | null, isPercent: boolean = false, fractionDigits: number = 1): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return '--';
    }
    if (isPercent) {
      return `${value.toFixed(fractionDigits)}%`;
    }
    return new Intl.NumberFormat('en-US').format(value);
  }

  getPercentWidth(value: number | null): number {
    if (value === null || value === undefined || Number.isNaN(value)) return 0;
    return Math.max(0, Math.min(100, value));
  }

  formatDateToString(date: Date | null): string {
    if (!date) return '';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  onPerformanceDateChange(dates: Date[]): void {
    this.performanceDateRange = dates || [];
    if (this.contestID) {
      this.getContestPerformance(this.contestID);
    }
  }

  getTrendBadge(trend: number | null | undefined): { value: number; isPositive: boolean } {
    const safe = typeof trend === 'number' && isFinite(trend) ? trend : 0;
    return { value: Math.abs(safe), isPositive: safe >= 0 };
  }

  getPercentageChange(current: number, previous: number): { value: number; isPositive: boolean } {
    if (previous === 0) {
      return { value: 0, isPositive: true };
    }
    const change = ((current - previous) / previous) * 100;
    return {
      value: Math.abs(change),
      isPositive: change >= 0
    };
  }
  getTimeRemaining(createdAt: string) {
    return this.sharedService.timeRemaining(createdAt);
  }
  // Get variables to display (limited or all)
  getDisplayedVariables(level: any): any[] {
    if (!level.client_variables || level.client_variables.length === 0) {
      return [];
    }
    const showAll = this.showAllVariables[level.id];
    return showAll ? level.client_variables : level.client_variables.slice(0, this.maxVariablesToShow);
  }
  shouldShowMore(level: any): boolean {
    return level.client_variables &&
      level.client_variables.length > this.maxVariablesToShow &&
      !this.showAllVariables[level.id];
  }
  shouldShowLess(level: any): boolean {
    return level.client_variables &&
      level.client_variables.length > this.maxVariablesToShow &&
      this.showAllVariables[level.id];
  }
  LevelUID(uid: string): void {
    if (uid) {
      this.clipboard.copy(uid);
      this.sharedService.createNotification('success', 'Level UID copied successfully!');
    } else {
      this.sharedService.createNotification('warning', 'No UID found for this level.');
    }
  }
  getRemainingVariablesCount(level: any): number {
    return level.client_variables ? level.client_variables.length - this.maxVariablesToShow : 0;
  }
  toggleVariablesDisplay(levelId: string): void {
    this.showAllVariables[levelId] = !this.showAllVariables[levelId];
  }
  private formatDate(timestamp: number | string): string {
    if (!timestamp) return 'N/A';

    const date = new Date(Number(timestamp));
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }) + ' ' + date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  private resolveDrawDate(contestData: any): string {
    const drawList = Array.isArray(contestData?.contest_draw) ? contestData.contest_draw : [];
    if (drawList.length > 0) {
      const latestDraw = [...drawList].sort(
        (a: any, b: any) => Number(b?.created_at || 0) - Number(a?.created_at || 0)
      )[0];
      if (latestDraw?.created_at) {
        return this.formatDate(latestDraw.created_at);
      }
    }

    return this.formatDate(contestData?.prize_announce_date);
  }

  copyUID(value: string): void {
    if (value) {
      this.clipboard.copy(value);
      this.sharedService.createNotification('success', 'Copied to clipboard successfully!');
    } else {
      this.sharedService.createNotification('warning', 'Nothing to copy.');
    }
  }

  copyLevelId(levelId: number): void {
    if (levelId) {
      this.clipboard.copy(levelId.toString());
      this.sharedService.createNotification('success', 'Level ID copied to clipboard!');
    }
  }

  // Helper method to check if value exists
  hasValue(value: any): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  // Toggle edit mode for a level
  onQuizDropdownOpen(isOpen: boolean): void {
    if (isOpen && !this.quizzesLoaded) {
      this.getQuizzesRecords();
    }
  }

  getQuizzesRecords(): void {
    if (this.quizzesLoaded) return;
    this.authService.getAll('GET_QUIZZES_LOOKUP').subscribe({
      next: (response: any) => {
        this.quizzesList = response || [];
        this.quizzesLoaded = true;
      },
      error: () => {
        this.sharedService.createNotification('error', 'Failed to load quizzes');
      },
    });
  }

  getQuizTitle(quizId: number): string {
    const quiz = this.quizzesList.find(q => q.id === quizId);
    return quiz?.quiz_title || 'Quiz #' + quizId;
  }

  toggleEditLevel(levelId: string, level: any): void {
    if (this.editingLevels[levelId]) {
      // Cancel editing
      this.editingLevels[levelId] = false;
      delete this.levelForms[levelId];
    } else {
      // Start editing - create form for this level
      this.editingLevels[levelId] = true;
      this.levelForms[levelId] = this.createLevelForm(level);
    }
  }

  // Create form for editing level
  createLevelForm(level: any): FormGroup {
    const levelType = level.level_type || level.type;
    const isTextLevel = levelType === 'text';

    const form = this.fb.group({
      name: [level.name || '', Validators.required],
      description: [level.description || '', Validators.required],
      textContent: this.fb.array(
        level.textContent && level.textContent.trim() !== ''
          ? this.splitTextContent(level.textContent).map((text: string) =>
            this.fb.control(text, isTextLevel ? [Validators.required, Validators.maxLength(1000)] : [Validators.maxLength(1000)])
          )
          : [this.fb.control('', isTextLevel ? [Validators.required, Validators.maxLength(1000)] : [Validators.maxLength(1000)])]
      ),
      qrData: [level.qrData || level.textContent || ''],
      client_webhook_url: [level.client_webhook_url || ''],
      imageFile: [null],
      client_variables: this.fb.array([]),
      quizzes_id: [level.quizzes_id || null],
    });

    // Set existing image preview if available
    if (level.image && level.image.url) {
      this.levelImagePreviews[level.id] = level.image.url;
    }

    // Pre-load quizzes if level type is quiz
    if ((level.level_type || level.type) === 'quiz' && !this.quizzesLoaded) {
      this.getQuizzesRecords();
    }

    // Add existing client variables if available
    if (level.client_variables && level.client_variables.length > 0) {
      const variablesArray = form.get('client_variables') as FormArray;
      level.client_variables.forEach((variable: any) => {
        variablesArray.push(this.fb.group({
          variable_name: [variable.variable_name || '', Validators.required],
          display_name: [variable.display_name || '', Validators.required],
          datatype: [variable.datatype || 'string', Validators.required],
        }));
      });
    }

    return form;
  }

  // Save updated level
  async saveLevel(levelId: string, level: any): Promise<void> {
    const form = this.levelForms[levelId];

    if (!form || form.invalid) {
      this.sharedService.createNotification('error', 'Please fill all required fields');
      form?.markAllAsTouched();
      return;
    }

    this.sharedService.showLoader();

    try {
      const formValue = form.value;
      const levelType = level.level_type || level.type;

      const updatePayload: any = {
        id: level.id,
        name: formValue.name,
        description: formValue.description,
        type: levelType,
        level_timeline: level.level_timeline,
        luqta_webhook_url: level.luqta_webhook_url || '',
      };

      // Add type-specific fields
      if (levelType === 'text') {
        const textContentArray = formValue.textContent || [];
        updatePayload.textContent = textContentArray.filter((text: string) => text.trim() !== '').join(' || ');
      } else if (levelType === 'qr') {
        updatePayload.qrData = formValue.qrData;
        updatePayload.textContent = formValue.qrData;
      } else if (levelType === 'image') {
        // Handle image upload - check preview first (has base64), then file
        const previewImage = this.levelImagePreviews[levelId];
        const imageFile = formValue.imageFile;

        if (previewImage && typeof previewImage === 'string' && previewImage.startsWith('data:image')) {
          // Use base64 from preview (newly uploaded image)
          updatePayload.image = { path: previewImage };
        } else if (imageFile && imageFile instanceof File) {
          // Convert file to base64 if preview not available
          const imageBase64 = await this.fileToBase64(imageFile);
          updatePayload.image = { path: imageBase64 };
        } else if (level.image) {
          // Keep existing image from server
          updatePayload.image = level.image;
        }
      } else if (levelType === 'client_webhook') {
        updatePayload.client_webhook_url = formValue.client_webhook_url || '';
        updatePayload.client_variables = formValue.client_variables || [];
      } else if (levelType === 'quiz') {
        updatePayload.quizzes_id = formValue.quizzes_id || null;
      } else {
        // For other types, preserve existing values
        updatePayload.client_webhook_url = '';
        updatePayload.client_variables = [];
      }

      // Update level via API
      const levelData = {
        level_details: [updatePayload],
        contest_id: this.contestID,
      };

      await firstValueFrom(
        this.authService.login('REQUEST_CONTEST_SET_LEVEL', levelData)
      );

      // Update local data
      const levelIndex = this.levels_of_contest.findIndex((l: any) => l.id === level.id);
      if (levelIndex !== -1) {
        const textContentArray = formValue.textContent || [];
        this.levels_of_contest[levelIndex] = {
          ...this.levels_of_contest[levelIndex],
          name: formValue.name,
          description: formValue.description,
          textContent: textContentArray.filter((text: string) => text.trim() !== '').join(' || '),
          qrData: formValue.qrData,
          client_webhook_url: formValue.client_webhook_url,
          client_variables: formValue.client_variables || [],
          quizzes_id: formValue.quizzes_id,
        };
      }

      this.sharedService.createNotification('success', 'Level updated successfully');
      this.editingLevels[levelId] = false;
      delete this.levelForms[levelId];
    } catch (error: any) {
      this.sharedService.showError(error, 'Failed to update level');
    } finally {
      this.sharedService.hideLoader();
    }
  }

  // Check if level is in edit mode
  isEditingLevel(levelId: string): boolean {
    return this.editingLevels[levelId] || false;
  }

  // Get form for a level
  getLevelForm(levelId: string): FormGroup | null {
    return this.levelForms[levelId] || null;
  }

  // Get textContent FormArray for a specific level
  getTextContents(levelId: string): FormArray {
    const levelForm = this.getLevelForm(levelId);
    return (levelForm?.get('textContent') as FormArray) ?? this.fb.array([]);
  }

  // Add a new text content field
  addTextContent(levelId: string): void {
    const textContents = this.getTextContents(levelId);
    textContents.push(this.fb.control('', [Validators.required, Validators.maxLength(1000)]));
  }

  // Remove a text content field
  removeTextContent(levelId: string, textIndex: number): void {
    const textContents = this.getTextContents(levelId);
    if (textContents && textContents.length > 1) {
      textContents.removeAt(textIndex);
    }
  }

  // Split textContent by || for display
  splitTextContent(textContent: string): string[] {
    if (!textContent || textContent.trim() === '') {
      return [];
    }
    return textContent
      .split(/\s*\|\|\s*/)
      .map((text) => text.trim())
      .filter((text) => text !== '');
  }

  // Get edit button text based on level type
  getEditButtonText(level: any): string {
    if (!level) return 'Edit Level';

    const levelType = level.level_type || level.type;

    switch (levelType) {
      case 'text':
        return 'Edit Text Level';
      case 'qr':
        return 'Edit QR-Code Level';
      case 'image':
        return 'Edit Image Level';
      case 'luqta_weebhook':
        return 'Edit Luqta Webhook';
      case 'client_webhook':
        return 'Edit Client Webhook';
      default:
        return 'Edit Level';
    }
  }

  // Trigger level image input
  triggerLevelImageInput(levelId: string): void {
    const fileInput = document.getElementById(`imageFile_${levelId}`) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  // Handle level image selection
  onLevelImageSelected(event: Event, levelId: string): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        this.levelImagePreviews[levelId] = reader.result;
        const form = this.levelForms[levelId];
        if (form) {
          form.patchValue({ imageFile: file });
        }
        // Show modal after image is uploaded
        this.showImageModal(levelId);
      };
      reader.readAsDataURL(file);
    }
  }

  // Show image preview modal
  showImageModal(levelId: string): void {
    this.isImageModalVisible[levelId] = true;
  }

  // Handle image modal OK
  handleImageOk(levelId: string): void {
    this.isImageModalVisible[levelId] = false;
  }

  // Handle image modal cancel
  handleImageCancel(levelId: string): void {
    this.isImageModalVisible[levelId] = false;
  }

  // Save image and keep preview visible
  async saveImageAndShowPreview(levelId: string, level: any): Promise<void> {
    const previewImage = this.levelImagePreviews[levelId];

    if (!previewImage || typeof previewImage !== 'string' || !previewImage.startsWith('data:image')) {
      this.sharedService.createNotification('error', 'Please upload an image first');
      return;
    }

    this.sharedService.showLoader();

    try {
      const levelType = level.level_type || level.type;

      const updatePayload: any = {
        id: level.id,
        name: level.name,
        description: level.description,
        type: levelType,
        level_timeline: level.level_timeline,
        luqta_webhook_url: level.luqta_webhook_url || '',
        image: { path: previewImage }
      };

      const levelData = {
        level_details: [updatePayload],
        contest_id: this.contestID,
      };

      await firstValueFrom(
        this.authService.login('REQUEST_CONTEST_SET_LEVEL', levelData)
      );

      // Update local level data immediately with preview image
      const levelIndex = this.levels_of_contest.findIndex((l: any) => l.id === level.id);
      if (levelIndex !== -1) {
        this.levels_of_contest[levelIndex] = {
          ...this.levels_of_contest[levelIndex],
          image: { url: previewImage, path: previewImage }
        };
      }

      this.sharedService.createNotification('success', 'Image saved successfully!');

      // Refresh contest details from server to get updated data
      await this.getContestDetails(this.contestID);

    } catch (error: any) {
      this.sharedService.showError(error, 'Failed to save image');
    } finally {
      this.sharedService.hideLoader();
    }
  }

  // Convert file to base64
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // Get client variables FormArray for a level
  getClientVariables(levelId: string): FormArray {
    const form = this.levelForms[levelId];
    return form?.get('client_variables') as FormArray;
  }

  // Add new variable to client webhook
  addVariable(levelId: string): void {
    const variables = this.getClientVariables(levelId);
    if (variables) {
      variables.push(this.fb.group({
        variable_name: ['', Validators.required],
        display_name: ['', Validators.required],
        datatype: ['string', Validators.required],
      }));
    }
  }

  // Remove variable from client webhook
  removeVariable(levelId: string, index: number): void {
    const variables = this.getClientVariables(levelId);
    if (variables && index >= 0 && index < variables.length) {
      variables.removeAt(index);
    }
  }

  // Helper method to get countries as array from location string
  getCountriesArray(location: string | null | undefined): string[] {
    if (!location || location.trim() === '') {
      return [];
    }
    // Split by comma, semicolon, or pipe and trim whitespace
    const countries = location.split(/[,;|]/).map(c => c.trim()).filter(c => c !== '');
    return countries;
  }

  // Search handler for participants
  onSearchChange(): void {
    this.searchSubject.next(this.searchTerm);
  }

  // Open filter modal for participants
  openFilterModal(): void {
    this.filterModalVisible = true;
  }

  // Handle filters applied from modal
  onFiltersApplied(filters: any): void {
    this.currentFilters = filters;
    this.applyClientFilters();
  }

  // Handle filter reset
  onFiltersReset(): void {
    this.currentFilters = {};
    this.applyClientFilters();
  }

  private getGenderFilterValues(selectedGender: string | null | undefined): string[] {
    const gender = String(selectedGender || '').trim().toLowerCase();

    if (gender === 'male') {
      return ['male', '\u0630\u0643\u0631'];
    }

    if (gender === 'female') {
      return ['female', '\u0623\u0646\u062b\u0649'];
    }

    if (gender === 'unspecified') {
      return [''];
    }

    if (gender === 'all') {
      return ['male', '\u0630\u0643\u0631', 'female', '\u0623\u0646\u062b\u0649', ''];
    }

    return [];
  }

  // Check if any filters are active
  hasActiveFilters(): boolean {
    return Object.keys(this.currentFilters).some(key => {
      const value = this.currentFilters[key];
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== null && value !== '' && value !== undefined;
    });
  }

  // Clear all filters
  clearAllFilters(): void {
    this.currentFilters = {};
    this.searchTerm = '';
    if (this.filterModalComponent) {
      this.filterModalComponent.filterForm.reset({
        dateRange: null, level: null, status: null, format: null,
        type: null, viewType: null, location: [], gender: null,
        contest: null, age: null, completionRate: null,
        participant_name: null, participant_email: null
      });
    }
    this.applyClientFilters();
  }

  // Apply client-side filtering on participants
  applyClientFilters(): void {
    let filtered = [...this.participantsList];
    const search = this.searchTerm?.trim().toLowerCase();
    const filters = this.currentFilters;

    // Search filter
    if (search) {
      filtered = filtered.filter(p => {
        const name = this.getParticipantName(p).toLowerCase();
        const gender = this.getParticipantGender(p).toLowerCase();
        const location = (p.contest?.location || '').toLowerCase();
        return name.includes(search) || gender.includes(search) || location.includes(search);
      });
    }

    // Status filter
    if (filters.status) {
      filtered = filtered.filter(
        p => String(p.status || p.participant_status || '').toLowerCase() === String(filters.status).toLowerCase()
      );
    }

    // Gender filter
    if (filters.gender) {
      const allowedGenderValues = this.getGenderFilterValues(filters.gender).map((value) =>
        value.toLowerCase().trim()
      );
      filtered = filtered.filter(p => {
        const participantGender = this.getParticipantGender(p);
        const normalizedGender = participantGender === 'N/A'
          ? ''
          : String(participantGender || '').toLowerCase().trim();
        return allowedGenderValues.includes(normalizedGender);
      });
    }

    // Location filter
    if (filters.location && Array.isArray(filters.location) && filters.location.length > 0) {
      filtered = filtered.filter(p => {
        const loc = (p.contest?.location || '').toLowerCase();
        return filters.location.some((l: string) => loc.includes(l.toLowerCase()));
      });
    }

    this.filteredParticipants = filtered;
  }

  // Open drawer to view participant's level progression
  openLevelsDrawer(participant: any): void {
    this.selectedParticipant = participant;
    // Get levels from the contest's levels_of_contest and map participant's progress
    this.selectedParticipantLevels = this.levels_of_contest.map((level: any, index: number) => {
      // Check if participant has completed this level
      const participantLevel = participant.levels_detail?.find((pl: any) => pl.level_id === level.id) ||
        participant.levelsDetail?.find((pl: any) => pl.level_id === level.id);
      return {
        name: level.name || `Level ${index + 1}`,
        status: participantLevel?.status || ((participant.status || participant.participant_status) === 'completed' ? 'completed' : 'pending'),
        points: participantLevel?.points || 0,
        completed_at: participantLevel?.completed_at || null,
        level_type: level.level_type || level.type,
        image: level.image,
        textContent: level.textContent,
        qrData: level.qrData,
        description: level.description
      };
    });
    this.levelsDrawerVisible = true;
  }

  // Close drawer
  closeLevelsDrawer(): void {
    this.levelsDrawerVisible = false;
    this.selectedParticipant = null;
    this.selectedParticipantLevels = [];
  }
}

